import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  Injectable,
  inject,
  provideAppInitializer,
  provideEnvironmentInitializer,
  LOCALE_ID,
} from '@angular/core';
import { environment } from '@environments/environment';
import { AppConfigService } from './app/core/services/config/app-config.service';
import { provideChangeDetection } from '@core/change-detection/change-detection-providers';
import * as Sentry from '@sentry/angular';
import {
  base64UrlSegmentEncode,
  base64UrlSegmentParse,
  GuidUrlSerializer,
} from '@core/serializers/base-64-guid-url-serializer';
import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import {
  withInterceptorsFromDi,
  provideHttpClient,
  withXhr,
} from '@angular/common/http';
import { bootstrapApplication } from '@angular/platform-browser';
import { CachingComponentRouteReuseStrategy } from './app/core/services/caching-component-route-reuse-strategy.service';
import {
  UrlSerializer,
  RouteReuseStrategy,
  provideRouter,
  PreloadAllModules,
  withPreloading,
  withRouterConfig,
  withDebugTracing,
  Router,
  RouterStateSnapshot,
  Params,
  withComponentInputBinding,
} from '@angular/router';
import { DatePipe, registerLocaleData } from '@angular/common';
import { CookieService } from 'ngx-cookie-service';
import { AuthClientConfig, AuthModule } from '@auth0/auth0-angular';
import { provideAuth } from 'angular-auth-oidc-client';
import {
  AUTH_PROVIDER,
  provideAuthProvider,
} from '@core/auth/auth-provider.token';
import { IAuthProvider } from '@core/auth/auth-provider.interface';
import {
  metaReducers,
  reducerProvider,
  reducerToken,
  RouterStateUrl,
  StateService,
} from '@core/state/state.service';
import { LogRocketService } from '@core/services/diagnostics/log-rocket.service';
import { HttpInterceptors } from '@core/http-interceptors';
import { ApplicationAccessService } from '@core/auth/application-access';
import {
  ActionReducer,
  META_REDUCERS,
  MetaReducer,
  provideStore,
  Store,
} from '@ngrx/store';
// Removed direct synchronous LogRocket references. Initialization handled in LogRocketService
// and meta-reducer below loads logrocket/logrocket-ngrx dynamically when enabled.
import { SignalRService } from '@core/services/signal-r/signal-r.service';
import {
  applyRuntimeFeatureFlagSnapshot,
  RuntimeFeatureFlagSnapshot,
  RuntimeFeatureFlagsService,
} from '@core/services/runtime-feature-flags.service';
import {
  DARK_THEME_CLASS,
  THEME_STORAGE_KEY,
  THEME_TOGGLE_STORAGE_KEY,
} from './app/core/services/theme/theme.service';
import { API_BASE_URL } from '@core/services/api-client.generated';
import { AnnotationFormService } from '@core/services/annotation-form/annotation-form.service';
import { StudiesTableHttpService } from '@core/services/study-table/study-table.service';
import { StageStudiesDataSource } from '@core/services/study-table/stage-studies-data-source.service';
import { provideRouterStore, RouterStateSerializer } from '@ngrx/router-store';
import { provideBootstrapEffects } from '@core/actions/util';
import { ProjectDetailEffects } from '@core/services/project/project-detail.effects';
import { AdminEffects } from '@core/services/admin/admin.effects';
import { ReviewEffects } from '@core/services/stage/review-effects';
import { StudyTableEffects } from '@core/services/study-table/study-table.effects';
import { AuthEffects } from '@core/auth/auth.effects';
import { DataExportEffects } from '@core/state/ui/data-export/data-export.effects';
import { EnvironmentEffects } from '@core/state/environment';
import { provideStudyPresenceSubscription } from '@app/study-presence/study-presence.init';
import { tap } from 'rxjs';
import { authApiActions } from '@core/auth/auth.actions';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { SentryErrorHandler } from '@core/services/diagnostics/sentry-error-handler.service';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ApmModule, ApmService } from '@elastic/apm-rum-angular';
import { apmInitializer } from '@core/services/diagnostics/apm-setup.service';
// (Handsontable cell types/plugins dynamically registered where needed.)
import {
  DateFnsLocaleLoader,
  DYNAMIC_DATE_FORMATS,
  DynamicLocaleDateAdapter,
} from '@core/dynamic-locale';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import localeFr from '@angular/common/locales/fr';
import localeGb from '@angular/common/locales/en-GB';
import localeDe from '@angular/common/locales/de';

// Pre-register core locales that are always bundled.
registerLocaleData(localeFr);
registerLocaleData(localeGb); // en-GB
registerLocaleData(localeDe);

declare global {
  interface Window {
    savedConsole: Console;
    base64UrlSegmentParse: (str: string) => string;
    base64UrlSegmentEncode: (str: string) => string;
  }
}

(window as any).base64UrlSegmentEncode = base64UrlSegmentEncode;
(window as any).base64UrlSegmentParse = base64UrlSegmentParse;
if (environment.production) {
  enableProdMode();
}

@Injectable()
export class CustomSerializer implements RouterStateSerializer<RouterStateUrl> {
  serialize(routerState: RouterStateSnapshot): RouterStateUrl {
    let route = routerState.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    const {
      url,
      root: { queryParams },
    } = routerState;
    const { params } = route;

    // Only return an object including the URL, params and query params
    // instead of the entire snapshot
    return { url, params, queryParams };
  }
}

const APP_EFFECTS = [
  ProjectDetailEffects,
  AdminEffects,
  ReviewEffects,
  StudyTableEffects,
  AuthEffects,
  DataExportEffects,
  EnvironmentEffects,
];
const clearThemePending = () => {
  if (typeof document !== 'undefined') {
    try {
      document.documentElement.classList.remove('syrf-theme-pending');
      if (!document.documentElement.classList.contains(DARK_THEME_CLASS)) {
        document.documentElement.style.colorScheme = 'light';
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
          metaThemeColor.setAttribute('content', '#ffffff');
        }
      }
    } catch {
      // Ignore DOM errors in non-browser contexts
    }
  }
};

const bootstrapFailed = (val: unknown) => {
  clearThemePending();
  // document.getElementById('bootstrap-fail').style.display = 'block';
  console.error('bootstrap-fail', val);
};

(async () => {
  try {
    const [defaultConfig, environmentConfig] = await Promise.all([
      fetch(
        `${environment.defaultConfigFile}?nocache=${new Date().getTime()}`
      ).then((res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to load ${environment.defaultConfigFile}: ${res.status}`
          );
        }
        return res.json();
      }),
      fetch(
        `${environment.envConfigFile}?nocache=${new Date().getTime()}`
      ).then((res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to load ${environment.envConfigFile}: ${res.status}`
          );
        }
        return res.json();
      }),
    ]);
    console.log('defaultConfig', defaultConfig);
    console.log('environmentConfig', environmentConfig);

    const mergedConfig = { ...defaultConfig, ...environmentConfig };
    AppConfigService.loadConfig(mergedConfig);
  } catch (error) {
    bootstrapFailed(error);
    return;
  }

  const applyTheme = (isThemeToggleEnabled: boolean) => {
    // Clear pending shell suppression independently so DOM visibility never depends on storage success
    clearThemePending();

    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(
          THEME_TOGGLE_STORAGE_KEY,
          isThemeToggleEnabled ? 'true' : 'false'
        );
      }
    } catch {
      // Ignore storage errors (e.g. QuotaExceededError or private browsing)
    }

    try {
      if (typeof document !== 'undefined') {
        if (isThemeToggleEnabled) {
          let stored: string | null = null;
          try {
            stored =
              typeof window !== 'undefined' && window.localStorage
                ? localStorage.getItem(THEME_STORAGE_KEY)
                : null;
          } catch {
            // Ignore storage errors
          }
          const prefersDark =
            window.matchMedia &&
            window.matchMedia('(prefers-color-scheme: dark)').matches;
          const isDark = stored === 'dark' || (stored !== 'light' && prefersDark);
          if (isDark) {
            document.documentElement.classList.add(DARK_THEME_CLASS);
            document.documentElement.style.colorScheme = 'dark';
            const metaThemeColor = document.querySelector('meta[name="theme-color"]');
            if (metaThemeColor) {
              metaThemeColor.setAttribute('content', '#121316');
            }
          } else {
            document.documentElement.classList.remove(DARK_THEME_CLASS);
            document.documentElement.style.colorScheme = 'light';
            const metaThemeColor = document.querySelector('meta[name="theme-color"]');
            if (metaThemeColor) {
              metaThemeColor.setAttribute('content', '#ffffff');
            }
          }
        } else {
          document.documentElement.classList.remove(DARK_THEME_CLASS);
          document.documentElement.style.colorScheme = 'light';
          const metaThemeColor = document.querySelector('meta[name="theme-color"]');
          if (metaThemeColor) {
            metaThemeColor.setAttribute('content', '#ffffff');
          }
        }
      }
    } catch {
      // Ignore DOM errors in non-browser contexts
    }
  };

  try {
    const response = await fetch(
      `${AppConfigService.staticConfig.apiUrl}/runtime-feature-flags`,
      { signal: AbortSignal.timeout(10_000) }
    );
    if (response.ok) {
      const snapshot = (await response.json()) as RuntimeFeatureFlagSnapshot;
      applyRuntimeFeatureFlagSnapshot(snapshot);
    } else {
      console.warn(
        `[Runtime Feature Flags] Initial snapshot request failed with ${response.status}; using deployed defaults.`
      );
    }
  } catch (error) {
    console.warn(
      '[Runtime Feature Flags] Initial snapshot request failed; using deployed defaults.',
      error
    );
  } finally {
    // Apply theme only after authoritative runtime overrides are known (or fallback to deployed defaults),
    // clearing any pending shell suppression and preventing premature paint of a non-effective flag.
    applyTheme(AppConfigService.staticConfig.themeToggle);
  }

  const config = AppConfigService.staticConfig;
  if (config.errorTracking) {
    Sentry.init({
      beforeSend: (event) =>
        AppConfigService.staticConfig.errorTracking ? event : null,
      beforeBreadcrumb: (breadcrumb) =>
        breadcrumb.category !== 'console' &&
        breadcrumb.category !== 'ui.click' &&
        (breadcrumb.category === 'action' ||
          (breadcrumb.level !== ('log' as Sentry.SeverityLevel) &&
            breadcrumb.level !== ('info' as Sentry.SeverityLevel)))
          ? breadcrumb
          : null,
      dsn: config.sentryDsn,
      environment: config.runtimeEnvironment,
      release: config.releaseVersion,
      maxBreadcrumbs: 40,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 1.0,
      tracePropagationTargets: [...config.protectedUrls],
    });
  }


  /**
   * Robust locale resolution & dynamic loading.
   * Ensures the app never crashes on pipes due to missing locale data (NG0701 / NG02100).
   * Strategy:
   *  1. Try full locale (e.g. es-ES)
   *  2. Try base language (e.g. es)
   *  3. Fallback to 'en-GB'
   * Dynamically imports only whitelisted locales to avoid bundling the entire Angular locale set.
   */
  const PRE_REGISTERED = new Set<string>(['en-GB', 'fr', 'de']);
  const DYNAMIC_WHITELIST = [
    'es',
    'es-ES',
    'it',
    'it-IT',
    'pt',
    'pt-PT',
    'pt-BR',
    'nl',
    'sv',
    'pl',
    'cs',
    'ru',
    'ja',
    'ko',
    'zh',
    'zh-CN',
    'zh-TW',
    'ar',
    'hi',
    'tr',
    'fi',
    'no',
    'da',
  ];
  const loadedDynamic = new Set<string>();

  async function loadLocaleData(code: string): Promise<boolean> {
    if (PRE_REGISTERED.has(code) || loadedDynamic.has(code)) return true;
    if (!DYNAMIC_WHITELIST.includes(code)) return false; // guard to keep bundle size small
    try {
      // Use switch to help bundler tree-shake unused locales (no broad dynamic expression).
      switch (code) {
        case 'es':
          (await import('@angular/common/locales/es')).default && registerLocaleData((await import('@angular/common/locales/es')).default);
          break;
        case 'es-ES':
          (await import('@angular/common/locales/es')).default && registerLocaleData((await import('@angular/common/locales/es')).default);
          break; // es & es-ES share data
        case 'it':
        case 'it-IT':
          registerLocaleData((await import('@angular/common/locales/it')).default);
          break;
        case 'pt':
        case 'pt-PT':
          registerLocaleData((await import('@angular/common/locales/pt')).default);
          break;
        case 'pt-BR':
          registerLocaleData((await import('@angular/common/locales/pt')).default);
          break;
        case 'nl':
          registerLocaleData((await import('@angular/common/locales/nl')).default);
          break;
        case 'sv':
          registerLocaleData((await import('@angular/common/locales/sv')).default);
          break;
        case 'pl':
          registerLocaleData((await import('@angular/common/locales/pl')).default);
          break;
        case 'cs':
          registerLocaleData((await import('@angular/common/locales/cs')).default);
          break;
        case 'ru':
          registerLocaleData((await import('@angular/common/locales/ru')).default);
          break;
        case 'ja':
          registerLocaleData((await import('@angular/common/locales/ja')).default);
          break;
        case 'ko':
          registerLocaleData((await import('@angular/common/locales/ko')).default);
          break;
        case 'zh':
        case 'zh-CN':
          registerLocaleData((await import('@angular/common/locales/zh')).default);
          break;
        case 'zh-TW':
          registerLocaleData((await import('@angular/common/locales/zh-Hant')).default);
          break;
        case 'ar':
          registerLocaleData((await import('@angular/common/locales/ar')).default);
          break;
        case 'hi':
          registerLocaleData((await import('@angular/common/locales/hi')).default);
          break;
        case 'tr':
          registerLocaleData((await import('@angular/common/locales/tr')).default);
          break;
        case 'fi':
          registerLocaleData((await import('@angular/common/locales/fi')).default);
          break;
        case 'no':
          registerLocaleData((await import('@angular/common/locales/nb')).default);
          break; // Norwegian Bokmål
        case 'da':
          registerLocaleData((await import('@angular/common/locales/da')).default);
          break;
        default:
          return false;
      }
      loadedDynamic.add(code);
      return true;
    } catch (e) {
      console.warn(
        '[i18n] Failed to dynamically load locale data for',
        code,
        e
      );
      return false;
    }
  }

  async function resolveLocale(raw: string): Promise<string> {
    if (!raw) raw = 'en-GB';
    const normalized = raw.replace('_', '-');
    const candidates = [normalized, normalized.split('-')[0]];
    for (const c of candidates) {
      if (PRE_REGISTERED.has(c)) return c; // already loaded
      const loaded = await loadLocaleData(c);
      if (loaded) return c;
    }
    // Fallback
    if (!PRE_REGISTERED.has('en-GB')) {
      // Should already be pre-registered via localeGb import, but guard just in case.
      try {
        registerLocaleData(
          (await import('@angular/common/locales/en-GB')).default
        );
      } catch {
        /* ignore */
      }
    }
    console.warn('[i18n] Falling back to en-GB for unsupported locale', raw);
    return 'en-GB';
  }

  const resolvedLocale = await resolveLocale(navigator.language);
  // Expose for diagnostics / potential user override.
  (window as any).__resolvedLocale = resolvedLocale;

  const authInit = (_authProvider: IAuthProvider, _store: Store) => () => {
    _authProvider.appState$
      .pipe(
        tap((appState) => {
          console.log('appstate: ', appState);
          _store.dispatch(authApiActions.authStateReceived({ appState }));
        })
      )
      .subscribe();
  };

  // Determine auth mode: BFF (cookie-based), OIDC (E2E/mock), or Auth0 (production)
  const useBff = config.authProvider === 'bff';
  const useOidc = config.authProvider === 'oidc';

  // Build auth-specific providers conditionally.
  // BFF mode: no client-side auth library needed; cookies handle everything.
  // OIDC mode: angular-auth-oidc-client for direct OIDC (legacy E2E / mock server).
  // Auth0 mode: Auth0 Angular SDK for production.
  const authModuleProviders = useBff
    ? [importProvidersFrom([ApmModule])]
    : useOidc
      ? [
          importProvidersFrom([ApmModule]),
          provideAuth({
            config: {
              authority: config.oidcAuthority,
              redirectUrl: globalThis.location.origin,
              postLogoutRedirectUri: globalThis.location.origin,
              clientId: config.oidcClientId,
              scope: config.oidcScope ?? 'openid profile email offline_access',
              responseType: 'code',
              silentRenew: true,
              useRefreshToken: true,
              secureRoutes: [...config.protectedUrls],
              autoUserInfo: true,
            },
          }),
        ]
      : [importProvidersFrom([AuthModule.forRoot(), ApmModule])];

  const bootstrap = () =>
    bootstrapApplication(AppComponent, {
      providers: [
        // Zone-based unless this deployment opted into zoneless. zone.js stays in the
        // polyfills while the switch exists, so a zoneless deployment logs NG0914.
        ...provideChangeDetection(config, environment),
        ...authModuleProviders,
        ...provideAuthProvider(),
        DatePipe,
        { provide: LOCALE_ID, useValue: resolvedLocale },
        CookieService,
        // Removed duplicate LOCALE_ID provider; dynamic resolvedLocale already supplied.
        DateFnsLocaleLoader,
        {
          provide: MAT_DATE_LOCALE,
          useValue: resolvedLocale,
        },
        {
          provide: DateAdapter,
          useClass: DynamicLocaleDateAdapter,
          deps: [MAT_DATE_LOCALE, DateFnsLocaleLoader],
        },
        {
          provide: MAT_DATE_FORMATS,
          useValue: DYNAMIC_DATE_FORMATS,
        },
        provideAppInitializer(() => {
          const initializerFn = ((
            appConfig: AppConfigService
          ) => {
            // Only configure Auth0 when in auth0 mode (not OIDC or BFF)
            const isAuth0 = appConfig.config.authProvider === 'auth0' || !appConfig.config.authProvider;
            if (isAuth0) {
              const authConfig = inject(AuthClientConfig);
              console.log('!configInitializer');
              console.log(appConfig.config);
              authConfig.set({
                domain: appConfig.config.auth0CustomDomain,
                clientId: appConfig.config.auth0ClientId,
                authorizationParams: {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  redirect_uri: window.location.origin,
                  scope: 'openid profile email offline_access',
                  audience: appConfig.config.auth0ApiAudience,
                },
                useRefreshTokens: true,
                useRefreshTokensFallback: true,
                cacheLocation: 'localstorage',
                httpInterceptor: {
                  allowedList: [
                    ...appConfig.config.protectedUrls.map(
                      (apiUrl) => `${apiUrl}/*`
                    ),
                    {
                      uri: `${appConfig.config.apiOrigin}/api/account/email-lookup-p`,
                      allowAnonymous: true,
                    },
                  ],
                },
              });
            }
            return () => {
              console.log('!promise');
              return new Promise<void>((resolve, reject) => {
                // Do some asynchronous stuff
                resolve();
              });
            };
          })(inject(AppConfigService));
          return initializerFn();
        }),
        {
          provide: Sentry.TraceService,
          deps: [Router],
        },
        provideAppInitializer(() => {
          const initializerFn = ((sentryTraceService) => () => {})(
            inject(Sentry.TraceService)
          );
          return initializerFn();
        }),
        reducerProvider,
        LogRocketService,
        ...HttpInterceptors,
        // M2a: keep the capability presentation (menus, guards, admin selectors) in step with
        // the backend from sign-in on, not only once an admin page is opened.
        provideEnvironmentInitializer(() => {
          inject(ApplicationAccessService);
        }),
        {
          provide: META_REDUCERS,
          multi: true,
          useFactory: (): MetaReducer => {
            if (!AppConfigService.staticConfig.logRocketEnabled) {
              return (original: ActionReducer<any>) => original;
            }
            // Lazy dynamic hook: wrap original reducer, and once logrocket + middleware loaded
            // swap to augmented reducer. Until then acts as pass-through so bootstrap stays sync.
            let delegated: ActionReducer<any> | null = null;
            (async () => {
              try {
                const [lrMod, ngrxMod] = await Promise.all([
                  import('logrocket'),
                  import('logrocket-ngrx' as any),
                ]);
                const lr: any = lrMod.default ?? lrMod; // UMD interop
                lr.init('1eww7o/syrf', {
                  release: AppConfigService.staticConfig.releaseVersion,
                });
                const creator: any =
                  (ngrxMod as any).default ||
                  (ngrxMod as any).createNgrxMiddleware ||
                  ngrxMod;
                if (typeof creator === 'function') {
                  // creator(lr) usually returns (reducer)=>wrappedReducer
                  delegated = creator(lr);
                }
              } catch (e) {
                console.warn('[logrocket] dynamic middleware load failed', e);
              }
            })();
            return (originalReducer: ActionReducer<any>) => {
              // If delegated is a higher-order reducer factory, apply it once we have it.
              if (
                delegated &&
                typeof delegated === 'function' &&
                (delegated as any).length
              ) {
                try {
                  const produced = (delegated as any)(originalReducer);
                  if (produced) {
                    delegated = produced; // now delegated is the actual reducer
                  }
                } catch {
                  /* ignore */
                }
              }
              return (state: any, action: any) => {
                if (
                  delegated &&
                  typeof delegated === 'function' &&
                  !(delegated as any).length
                ) {
                  // delegated already the concrete reducer
                  return (delegated as any)(state, action);
                }
                return originalReducer(state, action);
              };
            };
          },
        },
        provideAppInitializer(() => {
          const initializerFn = authInit(inject(AUTH_PROVIDER), inject(Store));
          return initializerFn();
        }),
        provideAppInitializer(() => {
          inject(RuntimeFeatureFlagsService);
        }),
        provideAppInitializer(() => {
          const initializerFn = ((_: SignalRService) => () => {})(
            inject(SignalRService)
          );
          return initializerFn();
        }),
        provideAppInitializer(() => {
          const initializerFn = apmInitializer();
          return initializerFn();
        }),
        {
          provide: API_BASE_URL,
          useFactory: (configService: AppConfigService) =>
            configService.config.apiOrigin,
          deps: [AppConfigService],
        },
        StateService,
        AnnotationFormService,
        StudiesTableHttpService,
        StageStudiesDataSource,
        { provide: RouterStateSerializer, useClass: CustomSerializer },

        provideBootstrapEffects(APP_EFFECTS),
        provideStudyPresenceSubscription(),
        {
          provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
          useValue: { appearance: 'outline', subscriptSizing: 'dynamic' },
        },
        provideStore(reducerToken, {
          metaReducers,
          runtimeChecks: {
            strictStateImmutability: true,
            strictActionImmutability: true,
          },
        }),

        {
          provide: UrlSerializer,
          useClass: GuidUrlSerializer,
        },
        {
          provide: ErrorHandler,
          useExisting: SentryErrorHandler,
        },
        {
          provide: RouteReuseStrategy,
          useClass: CachingComponentRouteReuseStrategy,
        },
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideAnimations(),
        provideRouter(
          appRoutes,
          withPreloading(PreloadAllModules),
          withComponentInputBinding(),
          withRouterConfig({
            paramsInheritanceStrategy: 'always',
            onSameUrlNavigation: 'reload',
          }),
          withDebugTracing(),
          withComponentInputBinding()
        ),
        provideRouterStore({ serializer: CustomSerializer }),
        provideStoreDevtools(() => ({
          name: `${AppConfigService.staticConfig.randomId}: ${AppConfigService.staticConfig.displayVersion}`,
          trace: true,
          traceLimit: 200,
        })),
      ],
    });
  bootstrap().catch(bootstrapFailed);
})().catch(bootstrapFailed);
